import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import {
  LayoutDashboard,
  Sparkles,
  Layers,
  HelpCircle,
  FileSpreadsheet,
  Users as UsersIcon,
  LogOut,
  RotateCcw,
  Menu,
  X,
  ChevronRight,
  Shield,
  CreditCard,
  Bell,
  BarChart3,
  Settings,
  Eye,
  Undo2,
} from 'lucide-react';
import { useToast } from '../ui/Toast';
import { Badge, IconButton, Button } from '../ui';

export interface AdminShellProps {
  children: React.ReactNode;
}

export const AdminShell: React.FC<AdminShellProps> = ({ children }) => {
  const {
    currentUser,
    currentView,
    setCurrentView,
    logoutUser,
    resetToDefaultData,
    returnToAdmin,
    setProfileModalOpen,
  } = useApp();
  const { showToast } = useToast();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const navGroups = [
    {
      title: 'Dashboard',
      items: [
        { id: 'admin-dashboard', label: 'Overview', icon: <LayoutDashboard className="w-4 h-4" /> },
      ],
    },
    {
      title: 'Content Management',
      items: [
        { id: 'admin-sectors', label: 'Sectors & Subjects', icon: <Sparkles className="w-4 h-4" /> },
        { id: 'admin-sets', label: 'Question Sets', icon: <Layers className="w-4 h-4" /> },
        { id: 'admin-questions', label: 'Question Bank & CSV', icon: <HelpCircle className="w-4 h-4" /> },
      ],
    },
    {
      title: 'User Management',
      items: [
        { id: 'admin-users', label: 'Registered Students', icon: <UsersIcon className="w-4 h-4" /> },
        { id: 'admin-attempts', label: 'Exam Submissions', icon: <FileSpreadsheet className="w-4 h-4" /> },
      ],
    },
    {
      title: 'SaaS Expansion & Commerce',
      items: [
        { id: 'admin-subscriptions', label: 'Subscriptions & Tiers', icon: <CreditCard className="w-4 h-4" />, disabled: false },
        { id: 'saas-notifications', label: 'Push & Alerts', icon: <Bell className="w-4 h-4" />, disabled: true },
        { id: 'saas-analytics', label: 'Revenue Analytics', icon: <BarChart3 className="w-4 h-4" />, disabled: true },
        { id: 'saas-settings', label: 'System Settings', icon: <Settings className="w-4 h-4" />, disabled: true },
      ],
    },
  ];

  const handleReturnToAdmin = async () => {
    const res = await returnToAdmin();
    if (res.success) {
      showToast('Restored admin session successfully', 'success', 'Session Restored');
    } else {
      showToast(res.error || 'Failed to return to admin', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F7FAFA] flex flex-col font-sans antialiased text-[#111111]">
      {/* Impersonation Banner */}
      {currentUser?.is_impersonating && (
        <div className="bg-[#8a6508] text-white px-4 py-2 text-xs font-bold flex items-center justify-between shadow-xs z-50">
          <div className="flex items-center gap-2 max-w-xl truncate">
            <Eye className="w-4 h-4 shrink-0 text-[#FFEAA7]" />
            <span className="truncate">
              Viewing as: <strong className="text-white underline">{currentUser.name}</strong> ({currentUser.email} — {currentUser.role})
            </span>
          </div>
          <button
            onClick={handleReturnToAdmin}
            className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 border border-white/40"
          >
            <Undo2 className="w-3.5 h-3.5" />
            Return to Admin
          </button>
        </div>
      )}

      {/* Top Header Bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#E5E9E9] h-16 flex items-center justify-between px-4 sm:px-6 shadow-xs">
        <div className="flex items-center gap-3">
          <IconButton
            icon={mobileSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            aria-label="Toggle Navigation Sidebar"
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="lg:hidden"
            size="sm"
          />
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#111111] text-white flex items-center justify-center font-bold text-xs p-1">
              <img src="/launch_icon.webp" alt="REVise" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-base font-extrabold text-[#111111] leading-none">REVise</span>
                <Badge variant="warning" size="sm">Admin</Badge>
              </div>
              <span className="text-[10px] text-[#5F6368] font-medium hidden sm:block">Operation & Content Control</span>
            </div>
          </div>
        </div>

        {/* Right Header Controls */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => {
              resetToDefaultData();
              showToast('Refreshed data from database', 'info');
            }}
            className="p-2 text-[#5F6368] hover:text-[#111111] hover:bg-[#F7FAFA] rounded-xl border border-transparent hover:border-[#E5E9E9] transition-all cursor-pointer"
            title="Refresh database records"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {currentUser && (
            <div className="flex items-center gap-2 border-l border-[#E5E9E9] pl-3">
              <button
                onClick={() => setProfileModalOpen(true)}
                className="flex items-center gap-2 p-1 rounded-xl hover:bg-[#F7FAFA] transition-all text-left"
              >
                <div className="w-7 h-7 rounded-lg bg-[#E8FAF9] text-[#1b8e88] font-bold text-xs flex items-center justify-center border border-[#56E8E1]/40 overflow-hidden">
                  {currentUser.profile_picture ? (
                    <img src={currentUser.profile_picture} alt={currentUser.name} className="w-full h-full object-cover" />
                  ) : (
                    currentUser.name.charAt(0)
                  )}
                </div>
                <span className="hidden sm:block text-xs font-bold text-[#111111] max-w-[120px] truncate">
                  {currentUser.name}
                </span>
              </button>
              <button
                onClick={() => {
                  logoutUser();
                  showToast('Signed out of admin session', 'info');
                }}
                className="p-1.5 text-[#5F6368] hover:text-[#D94A5C] hover:bg-[#FDEDEF] rounded-lg transition-colors cursor-pointer"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Body Layout with Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Persistent Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-[#E5E9E9] p-4 space-y-6 shrink-0">
          {navGroups.map((group) => (
            <div key={group.title} className="space-y-1.5">
              <h3 className="px-3 text-[10px] font-bold text-[#5F6368] uppercase tracking-wider">
                {group.title}
              </h3>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const isActive = currentView === item.id || (item.id === 'admin-sectors' && currentView === 'admin-subjects');
                  return (
                    <button
                      key={item.id}
                      disabled={item.disabled}
                      onClick={() => !item.disabled && setCurrentView(item.id as any)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
                        item.disabled
                          ? 'opacity-40 cursor-not-allowed text-[#9AA0A6]'
                          : isActive
                          ? 'bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40'
                          : 'text-[#5F6368] hover:text-[#111111] hover:bg-[#F7FAFA]'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className={isActive ? 'text-[#1fb4ac]' : 'text-[#80868B]'}>{item.icon}</span>
                        <span>{item.label}</span>
                      </div>
                      {item.disabled && (
                        <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.2 bg-[#F1F3F4] text-[#9AA0A6] rounded">
                          Soon
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </aside>

        {/* Mobile/Tablet Slide-Over Sidebar Drawer */}
        {mobileSidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-[#111111]/40 backdrop-blur-xs" onClick={() => setMobileSidebarOpen(false)} />
            <aside className="relative w-64 max-w-full bg-white border-r border-[#E5E9E9] p-4 space-y-6 z-10 overflow-y-auto">
              <div className="flex items-center justify-between pb-2 border-b border-[#E5E9E9]">
                <span className="text-xs font-extrabold text-[#111111] uppercase tracking-wider">Admin Menu</span>
                <IconButton icon={<X className="w-4 h-4" />} aria-label="Close menu" onClick={() => setMobileSidebarOpen(false)} size="sm" />
              </div>
              {navGroups.map((group) => (
                <div key={group.title} className="space-y-1.5">
                  <h3 className="px-3 text-[10px] font-bold text-[#5F6368] uppercase tracking-wider">
                    {group.title}
                  </h3>
                  <div className="space-y-1">
                    {group.items.map((item) => {
                      const isActive = currentView === item.id || (item.id === 'admin-sectors' && currentView === 'admin-subjects');
                      return (
                        <button
                          key={item.id}
                          disabled={item.disabled}
                          onClick={() => {
                            if (!item.disabled) {
                              setCurrentView(item.id as any);
                              setMobileSidebarOpen(false);
                            }
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
                            item.disabled
                              ? 'opacity-40 cursor-not-allowed text-[#9AA0A6]'
                              : isActive
                              ? 'bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40'
                              : 'text-[#5F6368] hover:text-[#111111] hover:bg-[#F7FAFA]'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <span className={isActive ? 'text-[#1fb4ac]' : 'text-[#80868B]'}>{item.icon}</span>
                            <span>{item.label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </aside>
          </div>
        )}

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
